import { useMemo, useCallback } from 'react';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { LocaleManager } from 'utils/locale/locale-manager';
import { SiteLanguageLabel, SiteNameSpace } from 'constants/locale';
import type { TenantId } from 'tenants/types/tenant';

/**
 * Centralized locale hook that provides all locale functionality.
 * Uses the LocaleManager for consistent behavior.
 */
export function useLocale(tenantId: TenantId) {
  const { t, ready } = useTranslation(SiteNameSpace.Common);
  const router = useRouter();

  // Create locale manager
  const localeManager = useMemo(
    () => LocaleManager.forClient(tenantId, router.locale),
    [tenantId, router.locale]
  );

  // Get current state
  const currentLocale = localeManager.getCurrentLocale();
  const savedPreference = localeManager.getSavedPreference();
  const isEnabled = localeManager.isLocalesSwitchingEnabled();
  const availableLocales = localeManager.getAvailableLocales();

  // Change locale function
  const changeLocale = useCallback(
    async (locale: string) => {
      await localeManager.switchToLocale(locale, router.asPath);
    },
    [localeManager, router.asPath]
  );

  // Create locale options for UI
  const localeOptions = useMemo(() => {
    return availableLocales.map(code => {
      const localeCode = typeof code === 'string' ? code : code;
      const labelKey = SiteLanguageLabel[localeCode as keyof typeof SiteLanguageLabel];
      
      // Format the display label based on the locale code
      const displayCode = localeCode.includes('-') ? localeCode.split('-')[1] : localeCode.toUpperCase();
      
      return {
        id: localeCode,
        label: labelKey ? `${t(labelKey)} (${displayCode})` : localeCode.toUpperCase(),
        isLink: false,
      };
    });
  }, [availableLocales, t]);

  return {
    // Current state
    currentLocale,
    savedPreference,
    isEnabled,
    isReady: ready,
    
    // Available options
    availableLocales,
    localeOptions,
    
    // Actions
    changeLocale,
    
    // Utilities
    localeManager,
  };
}
